import { KeyboardEvent } from "react"
import { ViewMode } from "../../../model/ViewMode"
import { multilineEditorService } from "../../../service/multilineEditorService"
import { isModPressed } from "../../../keyboard/platform"
import { EditorState, EditorAction } from "../types"

interface UseKeyboardShortcutsProps {
    state: EditorState
    value: string
    setValue: (newValue: string) => void
    onConfirm: () => void
    onCancel: () => void
    dispatch: React.Dispatch<EditorAction>
    snippets?: any[] // Using any temporarily
    onSnippetUsed?: (text: string) => void
    textAreaRef: React.RefObject<HTMLTextAreaElement | null>
    replaceWord: (suggestion: string) => void
    allowFullScreen?: boolean
}

export const useKeyboardShortcuts = ({
    state,
    value,
    setValue,
    onConfirm,
    onCancel,
    dispatch,
    snippets,
    onSnippetUsed,
    textAreaRef,
    replaceWord,
    allowFullScreen = true
}: UseKeyboardShortcutsProps) => {
    const insertIndentAtCursorPosition = (text: string, tryRemove: boolean) => {
        multilineEditorService.insertIndentAtCursorPosition(text, tryRemove, textAreaRef.current, setValue)
    }

    const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (isModPressed(event) && (event.code === 'Enter' || event.code === 'NumpadEnter')) {
            onConfirm()
        } else if (event.code === 'Escape') {
            onCancel()
        } else if (event.code === 'F4' && allowFullScreen) {
            const nextMode = multilineEditorService.getNextMode(state.viewMode)
            dispatch({ type: 'SET_VIEW_MODE', payload: nextMode })
        } else if (event.code === 'F9') {
            dispatch({ type: 'SET_AUTO_COMPLETE', payload: !state.autoComplete })
        } else if (event.code === 'F8' && snippets) {
            dispatch({ type: 'SET_ALL_SNIPPETS', payload: !state.allSnippets })
        } else if (event.key === 'Tab' && window.getSelection()) {
            event.preventDefault()
            insertIndentAtCursorPosition(value, event.shiftKey)
        }

        if (state.showSuggestions) {
            if (event.key === "ArrowDown") {
                event.preventDefault()
                dispatch({ type: 'SET_ACTIVE_INDEX', payload: (state.activeIdx + 1) % state.suggestions.length })
                return
            }
            if (event.key === "ArrowUp") {
                event.preventDefault()
                dispatch({
                    type: 'SET_ACTIVE_INDEX',
                    payload: (state.activeIdx - 1 + state.suggestions.length) % state.suggestions.length
                })
                return
            }
            if (event.key === "Enter") {
                event.preventDefault()
                replaceWord(state.suggestions[state.activeIdx])
                return
            }
            if (event.key === "Escape") {
                event.preventDefault()
                dispatch({ type: 'HIDE_SUGGESTIONS' })
                return
            }
        }
    }

    return {
        handleKeyDown
    }
}
